// FE-SCRM-048 — Recent imports table for the Settings → Import data
// section. Surfaces each session's type / when / who / status / counts
// and links back into the wizard via ``?session=<id>`` so a producer
// can review a session that's still in flight or finished.

import { Link } from "react-router-dom";

import { Table, TBody, THead, Th, Td, Tr } from "@/design-system";
import {
  DashboardEmptyState,
  DashboardErrorState,
  DashboardLoadingState,
} from "@/features/dashboard";

import { useImportListQuery } from "./hooks";
import {
  IMPORT_TYPE_LABEL,
  type ImportSession,
  type ImportStatus,
} from "./types";


const STATUS_LABEL: Record<ImportStatus, string> = {
  validating: "Validating",
  validated: "Ready to confirm",
  committing: "Importing…",
  completed: "Completed",
  failed: "Failed",
  expired: "Expired",
};


const STATUS_TONE: Record<ImportStatus, string> = {
  validating: "text-text-secondary",
  validated: "text-gold-ink",
  committing: "text-text-secondary",
  completed: "text-sage-ink",
  failed: "text-ember-ink",
  expired: "text-text-tertiary",
};


export function RecentImportsPanel() {
  const list_query = useImportListQuery();

  return (
    <section
      data-testid="recent-imports-panel"
      className="flex flex-col gap-2"
    >
      <header className="flex items-baseline justify-between">
        <h3 className="text-sm font-semibold text-text-primary">
          Recent imports
        </h3>
        <span className="text-xs text-text-tertiary">
          Sessions older than 24 hours have their source CSV expired.
        </span>
      </header>

      {list_query.isLoading && <DashboardLoadingState variant="card" />}
      {list_query.isError && (
        <DashboardErrorState
          title="Couldn't load recent imports"
          description="Refresh the page to retry."
          retry={() => list_query.refetch()}
        />
      )}
      {list_query.data && list_query.data.rows.length === 0 && (
        <DashboardEmptyState
          title="No imports yet"
          description={
            "Once you import prospects or contacts, the sessions will "
            + "appear here for re-download and audit."
          }
        />
      )}
      {list_query.data && list_query.data.rows.length > 0 && (
        <Table density="xs" dividers wrapperClassName="rounded-md border border-border-subtle">
          <THead className="bg-recessed text-text-tertiary">
            <Tr>
              <Th>Type</Th>
              <Th>When</Th>
              <Th>Status</Th>
              <Th>Counts</Th>
              <Th aria-label="Actions"></Th>
            </Tr>
          </THead>
          <TBody>
            {list_query.data.rows.map((row) => (
              <Row key={row.session_id} row={row} />
            ))}
          </TBody>
        </Table>
      )}

      {list_query.data && list_query.data.rows.length === 0 && (
        // Empty state already covers the "no imports" case; this
        // hidden testid mirrors the empty state for tests that need
        // to assert presence regardless of the underlying primitive.
        <div data-testid="recent-imports-empty" className="hidden" />
      )}
    </section>
  );
}


function Row({ row }: { row: ImportSession }) {
  const when = _formatWhen(row.created_at);
  const counts = _formatCounts(row);
  const status_label = STATUS_LABEL[row.status];
  const status_tone = STATUS_TONE[row.status];
  return (
    <Tr
      data-testid={`recent-import-row-${row.session_id}`}
      className="text-text-primary"
    >
      <Td>{IMPORT_TYPE_LABEL[row.import_type]}</Td>
      <Td className="text-text-secondary">{when}</Td>
      <Td className={status_tone}>{status_label}</Td>
      <Td className="text-text-secondary tabular-nums">{counts}</Td>
      <Td className="text-right">
        <Link
          to={`/dashboard/sales-crm/import?type=${row.import_type}&session=${row.session_id}`}
          className="text-xs font-medium text-teal hover:underline"
        >
          View
        </Link>
      </Td>
    </Tr>
  );
}


function _formatWhen(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: "short", day: "numeric",
    hour: "numeric", minute: "2-digit",
  });
}


function _formatCounts(row: ImportSession): string {
  const summary = row.commit_summary ?? {};
  if (row.import_type === "prospects") {
    if (typeof summary.prospects_created === "number") {
      return `${summary.prospects_created} created`;
    }
  } else {
    if (typeof summary.contacts_created === "number") {
      return `${summary.contacts_created} created`;
    }
  }
  const valid = row.validation_report?.valid_count ?? 0;
  const errors = row.validation_report?.error_count ?? 0;
  if (errors > 0) return `${errors} errors`;
  return `${valid} valid`;
}
